import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../common/types';
import { ReleasePlanDay } from '../../../../../redux/api/fymAPI.types';
import { planDayOn } from './planModel';
import { planMessages } from './planMessages';
import { planDayDate, planDayTitle, planOffsetLabel } from './rolloutDayCopy';
import { TimelineDay } from './TimelineDay';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RolloutTimelineProps {
  days: ReleasePlanDay[];
  kind: RolloutKind;
  /** The viewer's day, `YYYY-MM-DD`. */
  today: string;
  readOnly?: boolean;
  isAdding?: boolean;
  onAddDay?: (day: ReleasePlanDay) => void;
  /** Push mode: the ticked weeks, and the toggle of a week. */
  ticked?: ReadonlySet<string>;
  onToggleTick?: (day: ReleasePlanDay) => void;
}

/**
 * The rollout around the release date (design f1: −14 to +7) or the push weeks (design f2), one
 * column per day, with a "Today" mark before the first day of today or later.
 */
const RolloutTimelineComponent: React.FC<RolloutTimelineProps> = ({
  days,
  kind,
  today,
  readOnly,
  isAdding,
  onAddDay,
  ticked,
  onToggleTick,
}) => {
  const intl = useIntl();
  const rows = useMemo(
    () =>
      days.map((day, index) => {
        const on = planDayOn(day);
        const label =
          kind === 'release'
            ? planOffsetLabel(intl, day.offsetDays)
            : intl.formatMessage(planMessages.pushWeek, { week: index + 1 });
        return { day, on, label, title: planDayTitle(intl, day) };
      }),
    [days, intl, kind]
  );
  const todayIndex = useMemo(() => rows.findIndex((row) => row.on >= today), [rows, today]);

  return (
    <ol className={styles.timeline} data-testid="plan-timeline">
      {rows.map((row, index) => (
        <React.Fragment key={row.day.key}>
          {index === todayIndex && (
            <li className={styles.today} aria-label={intl.formatMessage(planMessages.today)}>
              {intl.formatMessage(planMessages.today)}
            </li>
          )}
          <TimelineDay
            day={row.day}
            label={row.label}
            dateLabel={planDayDate(intl, row.on)}
            title={row.title}
            isPast={row.on < today}
            isReleaseDay={kind === 'release' && row.day.offsetDays === 0}
            readOnly={readOnly}
            isAdding={isAdding}
            onAddDay={onAddDay}
            tick={
              ticked === undefined || onToggleTick === undefined || !row.day.gap
                ? undefined
                : {
                    isOn: ticked.has(row.day.key),
                    label: intl.formatMessage(planMessages.pushWeekToggle, { week: index + 1 }),
                    onToggle: onToggleTick,
                  }
            }
          />
        </React.Fragment>
      ))}
    </ol>
  );
};

export const RolloutTimeline = TypedMemo(RolloutTimelineComponent);
